import { useEffect, useState } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";

const EMPTY = { title: "", category: "compatibility", excerpt: "", content: "", cover_image: "", related_brand: "", meta_title: "", meta_description: "", slug: "" };

function slugify(s) { return s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""); }

function RichEditor({ value, onChange }) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value || "<p></p>",
    editorProps: { attributes: { class: "prose prose-sm max-w-none min-h-[200px] focus:outline-none p-4" } },
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
  });
  return (
    <div className="border border-xd-border rounded-md overflow-hidden">
      <div className="flex gap-1 border-b border-xd-border p-2 bg-xd-light text-xs">
        <button type="button" onClick={() => editor?.chain().focus().toggleBold().run()} className="px-2 py-1 hover:bg-white rounded font-bold">B</button>
        <button type="button" onClick={() => editor?.chain().focus().toggleItalic().run()} className="px-2 py-1 hover:bg-white rounded italic">I</button>
        <button type="button" onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} className="px-2 py-1 hover:bg-white rounded">H2</button>
        <button type="button" onClick={() => editor?.chain().focus().toggleBulletList().run()} className="px-2 py-1 hover:bg-white rounded">•</button>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const load = () => api.get("/admin/blog").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditing(p); };

  const save = async () => {
    try {
      const payload = { ...form, slug: form.slug || slugify(form.title) };
      if (editing?.id) await api.put(`/admin/blog/${editing.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success("Salvato");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const del = async (p) => {
    if (!window.confirm("Eliminare?")) return;
    await api.delete(`/admin/blog/${p.id}`);
    toast.success("Eliminato"); load();
  };

  return (
    <AdminLayout>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">Contenuti</div>
          <h1 className="font-admin text-2xl font-bold">Blog</h1>
        </div>
        <button type="button" onClick={openNew} data-testid="blog-new-btn" className="btn-cta rounded-lg px-4 h-9 text-sm inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Nuovo articolo</button>
      </div>
      <div className="bg-white border border-xd-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-xd-light border-b border-xd-border">
            <tr>{["Titolo", "Categoria", "Marca correlata", "Pubblicato", ""].map((h) => <th key={h} className="px-4 py-3 text-left font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-xd-border last:border-b-0 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium">{p.title}</td>
                <td className="px-4 py-3 text-xs">{p.category}</td>
                <td className="px-4 py-3 text-xs text-xd-muted">{p.related_brand || "—"}</td>
                <td className="px-4 py-3 text-xs">{new Date(p.published_at).toLocaleDateString("it-IT")}</td>
                <td className="px-4 py-3 text-right">
                  <button type="button" onClick={() => openEdit(p)} className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 transition-colors"><Pencil className="w-5 h-5" /></button>
                  <button type="button" onClick={() => del(p)} className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 border border-red-200 transition-colors"><Trash2 className="w-5 h-5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-admin">{editing?.id ? "Modifica articolo" : "Nuovo articolo"}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Input placeholder="Titolo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.slug || slugify(e.target.value) })} data-testid="blog-title" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="border border-xd-border rounded-md px-3 py-2 text-sm">
                <option value="compatibility">Guide compatibilità</option>
                <option value="installation">Guide installazione</option>
                <option value="news">Novità prodotto</option>
              </select>
              <Input placeholder="Slug URL" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              <Input placeholder="Marca correlata (Fiat, Audi…)" value={form.related_brand || ""} onChange={(e) => setForm({ ...form, related_brand: e.target.value })} />
              <Input placeholder="URL immagine copertina" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} />
              <Input placeholder="Meta title (SEO)" value={form.meta_title || ""} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} className="col-span-2" />
              <Input placeholder="Meta description (SEO)" value={form.meta_description || ""} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} className="col-span-2" />
            </div>
            <textarea placeholder="Estratto" rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="border border-xd-border rounded-md px-3 py-2 text-sm" />
            <div>
              <div className="text-[10px] font-mono-tech uppercase tracking-widest text-xd-muted mb-1">Contenuto</div>
              <RichEditor value={form.content} onChange={(v) => setForm({ ...form, content: v })} />
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 text-sm">Annulla</button>
            <button type="button" onClick={save} data-testid="blog-save" className="btn-cta rounded-lg px-4 py-2 text-sm">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
